/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {useMemo} from 'react'
import {useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'

// Other Hooks
import {useVariationParams} from './use-variation-params'

// Utils
import {productUrlBuilder, updateSearchParams} from '../utils/url'
import {usePDPSearchParams} from './use-pdp-search-params'

/**
 * Return the first image in the `swatch` type image group for a given
 * variation value of a product.
 *
 * @param {Object} product
 * @param {Object} variationValue
 * @returns {Object} image
 */
const getVariantValueSwatch = (product = {}, variationValue) => {
    const {imageGroups = []} = product

    const imageGroup = imageGroups
        .filter(({viewType}) => viewType === 'swatch')
        .find(({variationAttributes = []}) => {
            const colorAttribute = variationAttributes.find(({id}) => id === 'color')
            const colorValues = colorAttribute?.values || []

            // A single image can represent multiple variation values, so we only need
            // ensure the variation values appears in once of the images represented values.
            return colorValues.some(({value}) => value === variationValue.value)
        })

    return imageGroup?.images?.[0]
}

/**
 * Build a product href
 *
 * @param {Object} product
 * @param {Object} params
 * @param {Object} location
 * @returns {String} a product url for the current variation value.
 */
const buildVariantValueHref = ({
    pathname,
    existingParams,
    newParams,
    productId,
    isProductPartOfSet
}) => {
    const [allParams, productParams] = existingParams

    if (isProductPartOfSet) {
        updateSearchParams(productParams, newParams)
        allParams.set(productId, productParams.toString())
    } else {
        updateSearchParams(allParams, newParams)
    }

    return `${pathname}?${allParams.toString()}`
}

/**
 * Determine if a products variant attribute value is orderable without having to
 * load the variant in question, but filtering the list of variants with the
 * passed in attribute values.
 *
 * @param {Object} product
 * @param {Object} variationParams
 * @returns
 */
const isVariantValueOrderable = (product = {}, variationParams) => {
    const {variants = []} = product

    return variants
        .filter(({variationValues}) =>
            Object.keys(variationParams).every(
                (key) => variationValues[key] === variationParams[key]
            )
        )
        .some(({orderable}) => orderable)
}

/**
 * Use a decorated version of a product variation attributes. This version
 * will have the following additions: which variation attribute is selected,
 * each value will have a product url, the swatch image if there is one, and
 * an updated orderable flag.
 *
 * @param {Object} product
 * @param {Boolean} isProductPartOfSet
 * @returns {Array} a decorated variation attributes list.
 *
 */
export const useVariationAttributes = (product, isProductPartOfSet = false) => {
    const {variationAttributes = []} = product || {}
    const location = useLocation()
    const variationParams = useVariationParams(product, isProductPartOfSet)
    const productId = useMemo(
        () => (isProductPartOfSet ? product?.setProductId : product?.id),
        [isProductPartOfSet, product?.id, product?.setProductId]
    )

    const existingParams = usePDPSearchParams(productId)

    return useMemo(
        () =>
            variationAttributes
                .map((variationAttribute) => ({
                    ...variationAttribute,
                    selectedValue: {
                        ...variationAttribute.values?.find(
                            ({value}) => value === variationParams?.[variationAttribute.id]
                        ),
                        value: variationParams?.[variationAttribute.id]
                    },
                    values: variationAttribute.values?.map((value) => {
                        const params = {
                            ...variationParams,
                            [variationAttribute.id]: value.value
                        }

                        const pid = product?.variants?.find(
                            ({variationValues}) => variationValues.color === value.value
                        )?.productId

                        return {
                            ...value,
                            image: getVariantValueSwatch(product, value),
                            href: buildVariantValueHref({
                                pathname: location.pathname,
                                existingParams,
                                newParams: {pid, ...params},
                                productId,
                                isProductPartOfSet
                            }),
                            orderable: isVariantValueOrderable(product, params),
                            productId: pid
                        }
                    })
                }))
                // deactivated products doesn't have variants, so we need to filter them out
                .filter((variationAttribute) => variationAttribute.values?.length > 0),
        [
            existingParams,
            isProductPartOfSet,
            location.pathname,
            product,
            productId,
            variationAttributes,
            variationParams
        ]
    )
}

/**
 * Return the color variation attribute for a product.
 * @param {Object} product
 * @param {Boolean} [isProductPartOfSet] - whether the product is part of a set
 * @returns {Object} a decorated color variation attribute
 */
export const useColorVariation = (product, isProductPartOfSet) => {
    const variationAttributes = useVariationAttributes(product, isProductPartOfSet)
    return useMemo(
        () => variationAttributes?.filter(({id}) => id === 'color').shift(),
        [variationAttributes]
    )
}

/**
 * Return the grouped color variation attribute for a product.
 * @param {Object} product
 * @param {Boolean} [isProductPartOfSet] - whether the product is part of a set
 * @returns {Object} a decorated color variation attribute
 */
export const useGroupedColorVariation = (product, isProductPartOfSet) => {
    const colorVariation = useColorVariation(product, isProductPartOfSet)
    const colorGroups = useMemo(() => {
        let groups = []
        if (product.c_variantGrouping) {
            try {
                const variantGroups = JSON.parse(product?.c_variantGrouping)
                const mainGroupProductIds = []
                // variants without promotions should be first
                Object.values(variantGroups).forEach((variantGroup) => {
                    if (!variantGroup.promotions) {
                        mainGroupProductIds.push(...variantGroup.variants)
                    }
                })
                const mainGroupValues = colorVariation.values.filter((value) => {
                    return mainGroupProductIds.includes(value.productId)
                })
                if (mainGroupValues.length > 0) {
                    groups.push({
                        values: mainGroupValues,
                        label: colorVariation.name,
                        displayName: colorVariation.selectedValue.name
                    })
                }
                // variants with promotions should be next
                Object.values(variantGroups).forEach((variantGroup) => {
                    if (variantGroup.promotions) {
                        const promoGroupValues = colorVariation.values.filter((value) => {
                            return variantGroup.variants.includes(value.productId)
                        })
                        if (promoGroupValues.length > 0) {
                            groups.push({values: promoGroupValues, label: variantGroup.label})
                        }
                    }
                })
            } catch (e) {
                console.error('Error parsing product variation groups', e)
                // all variants will be rendered in case of invalid JSON stricture
                groups = [
                    {
                        values: colorVariation.values,
                        label: colorVariation.name,
                        displayName: colorVariation.selectedValue.name
                    }
                ]
            }
        } else {
            // all variants will be rendered in case of missing JSON stricture
            groups = [
                {
                    values: colorVariation.values,
                    label: colorVariation.name,
                    displayName: colorVariation.selectedValue.name
                }
            ]
        }
        return groups
    }, [
        product.c_variantGrouping,
        colorVariation.values,
        colorVariation.name,
        colorVariation.selectedValue.name
    ])
    return {
        groups: colorGroups,
        selectedValue: colorVariation.selectedValue
    }
}

/**
 * Return the material variation attribute for a product if any.
 * @param {Object} product
 */
export const useMaterialVariation = (product) => {
    const {locale} = useIntl()
    return useMemo(() => {
        if (!product?.c_variationValuesProductSelector) {
            return null
        }

        const selected = product.c_variationValuesProductSelector.find(
            (variant) => variant.isSelected
        )

        return {
            id: 'material',
            name: product.c_productLinkVariationName,
            selectedValue: {
                name: selected?.subType,
                value: selected?.ID
            },
            values: product.c_variationValuesProductSelector.map((variant) => ({
                href: productUrlBuilder({id: variant.ID}, locale, variant?.slugUrl),
                image: {
                    alt: variant.displayName,
                    disBaseLink: variant.swatchImageUrl,
                    link: variant.swatchImageUrl,
                    title: variant.displayName
                },
                name: variant.subType,
                orderable: variant.isSelectable,
                value: variant.ID
            }))
        }
    }, [locale, product])
}
